import { coinGeckoAxios } from '../config';
import { AssetConfig, DEFAULT_ASSET } from '../config/assets';

const PRICE_CACHE_KEY_PREFIX = 'qache:lastPriceEur';
const PRICE_CACHE_AT_KEY_PREFIX = 'qache:lastPriceEurAt';
const MIN_REFETCH_INTERVAL_MS = 15000;

// In-memory cache: keyed by coingeckoId
let cachedPrices: Partial<Record<string, number | null>> = {};
let cachedAts: Partial<Record<string, number>> = {};
let inFlightRequests: Partial<Record<string, Promise<number | null>>> = {};

const isValidPrice = (value: number) => isFinite(value) && value > 0;

const readStoredNumber = (key: string): number => {
  if (typeof window === 'undefined') return 0;
  const parsed = Number(window.localStorage.getItem(key));
  return isFinite(parsed) ? parsed : 0;
};

const persistCache = (coingeckoId: string, price: number, at: number) => {
  cachedPrices[coingeckoId] = price;
  cachedAts[coingeckoId] = at;
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(`${PRICE_CACHE_KEY_PREFIX}:${coingeckoId}`, String(price));
  window.localStorage.setItem(`${PRICE_CACHE_AT_KEY_PREFIX}:${coingeckoId}`, String(at));
};

const loadCacheFromStorage = (coingeckoId: string) => {
  if (cachedPrices[coingeckoId] !== undefined) return;
  const storedPrice = readStoredNumber(`${PRICE_CACHE_KEY_PREFIX}:${coingeckoId}`);
  if (!isValidPrice(storedPrice)) return;
  cachedPrices[coingeckoId] = storedPrice;
  cachedAts[coingeckoId] = readStoredNumber(`${PRICE_CACHE_AT_KEY_PREFIX}:${coingeckoId}`);
};

/**
 * Get cached EUR price for an asset (does not fetch; returns null if not cached).
 */
export const getCachedEurRate = (asset: AssetConfig = DEFAULT_ASSET): number | null => {
  loadCacheFromStorage(asset.coingeckoId);
  return isValidPrice(Number(cachedPrices[asset.coingeckoId])) ? Number(cachedPrices[asset.coingeckoId]) : null;
};

/**
 * Fetch live EUR price for an asset from CoinGecko, with in-memory + localStorage caching,
 * 15s minimum refetch interval, and in-flight request deduplication.
 * Falls back to cached price on error.
 */
export const fetchEurRate = async (asset: AssetConfig = DEFAULT_ASSET, force = false): Promise<number | null> => {
  const { coingeckoId } = asset;
  loadCacheFromStorage(coingeckoId);
  const now = Date.now();

  if (!force && cachedPrices[coingeckoId] !== undefined && now - (cachedAts[coingeckoId] || 0) < MIN_REFETCH_INTERVAL_MS) {
    return cachedPrices[coingeckoId];
  }

  if (inFlightRequests[coingeckoId]) {
    return inFlightRequests[coingeckoId];
  }

  inFlightRequests[coingeckoId] = (async () => {
    try {
      const response = await coinGeckoAxios.get('simple/price', {
        params: {
          ids: coingeckoId,
          vs_currencies: 'eur',
        },
      });
      const price = Number(response?.data?.[coingeckoId]?.eur);
      if (!isValidPrice(price)) {
        throw new Error(`Invalid price payload for ${coingeckoId}`);
      }
      persistCache(coingeckoId, price, Date.now());
      return price;
    } catch (error) {
      console.error(`Error fetching ${coingeckoId} price:`, error);
      return cachedPrices[coingeckoId] ?? null;
    } finally {
      delete inFlightRequests[coingeckoId];
    }
  })();

  return inFlightRequests[coingeckoId];
};

/**
 * Convenience: fetch ETH/EUR rate (deprecated, use fetchEurRate with ASSETS.ETH if ETH support is re-added).
 * Currently just an alias to fetchEurRate(DEFAULT_ASSET).
 */
export const fetchEthPriceEur = async (force = false): Promise<number | null> => {
  return fetchEurRate(DEFAULT_ASSET, force);
};

/**
 * Convenience: get cached ETH/EUR rate (deprecated, use getCachedEurRate with ASSETS.ETH if ETH support is re-added).
 * Currently just an alias to getCachedEurRate(DEFAULT_ASSET).
 */
export const getCachedEthPriceEur = (): number | null => {
  return getCachedEurRate(DEFAULT_ASSET);
};
